/**
 * Intelligent lazy loading utilities for mobile optimization
 */

import { lazy, ComponentType, LazyExoticComponent } from 'react';

// Preloading strategies
export type PreloadStrategy = 'immediate' | 'hover' | 'viewport' | 'idle' | 'never';

// Component loading priorities
export type LoadingPriority = 'high' | 'medium' | 'low';

// Lazy loading configuration
export interface LazyLoadConfig {
  preloadStrategy?: PreloadStrategy;
  priority?: LoadingPriority;
  timeout?: number;
  retries?: number;
  fallback?: ComponentType;
  chunkName?: string;
}

// Component registry for preloading
const componentRegistry = new Map<string, () => Promise<any>>();
const preloadedComponents = new Set<string>();

/**
 * Enhanced lazy loading with intelligent preloading strategies
 */
export function createLazyComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  config: LazyLoadConfig = {}
): LazyExoticComponent<T> {
  const {
    preloadStrategy = 'viewport',
    priority = 'medium',
    timeout = 10000,
    retries = 3,
    chunkName
  } = config;

  // Create a wrapper for the import function with retry logic
  const importWithRetry = async (): Promise<{ default: T }> => {
    let lastError: Error;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const module = await Promise.race([
          importFn(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Import timeout')), timeout)
          )
        ]);
        return module;
      } catch (error) {
        lastError = error as Error;
        
        // Wait before retrying (exponential backoff)
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    throw lastError!;
  };

  // Register component for preloading
  if (chunkName) {
    componentRegistry.set(chunkName, importWithRetry);
  }

  // Create lazy component
  const LazyComponent = lazy(importWithRetry);

  // Set up preloading based on strategy
  setupPreloading(importWithRetry, preloadStrategy, priority, chunkName);

  return LazyComponent;
}

/**
 * Setup preloading based on strategy
 */
function setupPreloading(
  importFn: () => Promise<any>,
  strategy: PreloadStrategy,
  _priority: LoadingPriority,
  chunkName?: string
): void {
  switch (strategy) {
    case 'immediate':
      // Preload immediately
      preloadComponent(importFn, chunkName);
      break;
      
    case 'idle':
      // Preload when browser is idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => preloadComponent(importFn, chunkName));
      } else {
        setTimeout(() => preloadComponent(importFn, chunkName), 1000);
      }
      break;
      
    case 'hover':
    case 'viewport':
      // These are handled by specific hooks/components
      break;
      
    case 'never':
      // No preloading
      break;
  }
}

/**
 * Preload a component
 */
export async function preloadComponent(
  importFn: () => Promise<any>,
  chunkName?: string
): Promise<void> {
  if (chunkName && preloadedComponents.has(chunkName)) {
    return; // Already preloaded
  }

  try {
    await importFn();
    if (chunkName) {
      preloadedComponents.add(chunkName);
    }
  } catch (error) {
    console.warn('Failed to preload component:', error);
  }
}

/**
 * Preload component on hover
 */
export function useHoverPreload(chunkName: string) {
  return {
    onMouseEnter: () => {
      const importFn = componentRegistry.get(chunkName);
      if (importFn) {
        preloadComponent(importFn, chunkName);
      }
    }
  };
}

/**
 * Preload component when in viewport
 */
export function useViewportPreload(chunkName: string, threshold = 0.1) {
  const preload = () => {
    const importFn = componentRegistry.get(chunkName);
    if (importFn) {
      preloadComponent(importFn, chunkName);
    }
  };

  return {
    ref: (element: HTMLElement | null) => {
      if (!element || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              preload();
              observer.disconnect();
            }
          });
        },
        { threshold }
      );

      observer.observe(element);
    }
  };
}

/**
 * Intelligent resource preloading based on connection and device
 */
export class IntelligentPreloader {
  private static instance: IntelligentPreloader;
  private preloadQueue: Array<{ importFn: () => Promise<any>; priority: LoadingPriority; chunkName?: string }> = [];
  private isProcessing = false;
  private connectionInfo: any;

  private constructor() {
    this.connectionInfo = (navigator as any).connection;
    this.startProcessing();
  }

  static getInstance(): IntelligentPreloader {
    if (!IntelligentPreloader.instance) {
      IntelligentPreloader.instance = new IntelligentPreloader();
    }
    return IntelligentPreloader.instance;
  }

  /**
   * Add component to preload queue
   */
  addToQueue(
    importFn: () => Promise<any>,
    priority: LoadingPriority = 'medium',
    chunkName?: string
  ): void {
    // Check if already preloaded
    if (chunkName && preloadedComponents.has(chunkName)) {
      return;
    }

    // Add to queue with priority
    this.preloadQueue.push({ importFn, priority, chunkName });
    
    // Sort by priority
    this.preloadQueue.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * Process preload queue intelligently
   */
  private async startProcessing(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.preloadQueue.length > 0) {
      // Check if we should continue preloading
      if (!this.shouldContinuePreloading()) {
        await this.waitForBetterConditions();
        continue;
      }

      const item = this.preloadQueue.shift();
      if (!item) continue;

      try {
        await preloadComponent(item.importFn, item.chunkName);
        
        // Add delay between preloads to avoid blocking
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.warn('Failed to preload component in queue:', error);
      }
    }

    this.isProcessing = false;
  }

  /**
   * Check if we should continue preloading based on device/network conditions
   */
  private shouldContinuePreloading(): boolean {
    // Check battery level
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        if (battery.level < 0.2 && !battery.charging) {
          return false; // Low battery, pause preloading
        }
      });
    }

    // Check connection
    if (this.connectionInfo) {
      const { effectiveType, saveData, downlink } = this.connectionInfo;
      
      // Don't preload on slow connections or data saver mode
      if (effectiveType === '2g' || effectiveType === 'slow-2g' || saveData || downlink < 1) {
        return false;
      }
    }

    // Check if page is visible
    if (document.hidden) {
      return false;
    }

    return true;
  }

  /**
   * Wait for better conditions to continue preloading
   */
  private async waitForBetterConditions(): Promise<void> {
    return new Promise((resolve) => {
      const checkConditions = () => {
        if (this.shouldContinuePreloading()) {
          resolve();
        } else {
          setTimeout(checkConditions, 5000); // Check again in 5 seconds
        }
      };
      
      setTimeout(checkConditions, 1000);
    });
  }
}

/**
 * Hook for intelligent preloading
 */
export function useIntelligentPreload() {
  const preloader = IntelligentPreloader.getInstance();
  
  return {
    preload: (
      importFn: () => Promise<any>,
      priority: LoadingPriority = 'medium',
      chunkName?: string
    ) => {
      preloader.addToQueue(importFn, priority, chunkName);
    }
  };
}

/**
 * Critical resource preloader for above-the-fold content
 * Optimized to reduce initial load time and improve LCP
 */
export function preloadCriticalResources(): void {
  const connection = (navigator as any).connection;
  const isSlowConnection = connection && (
    connection.effectiveType === '2g' || 
    connection.effectiveType === 'slow-2g' ||
    connection.downlink < 1.5 ||
    connection.saveData
  );
  
  // Only preload essentials on slow connections
  if (isSlowConnection) {
    return; // Skip all preloading on slow connections
  }

  // No font preload: the app uses system fonts and ships no Inter font asset.

  // Delay other preloads to not compete with critical resources
  setTimeout(() => {
    // Only preload hero image on fast connections
    if (connection && connection.effectiveType === '4g' && connection.downlink > 2) {
      const heroImage = document.createElement('link');
      heroImage.rel = 'preload';
      heroImage.as = 'image';
      heroImage.href = '/images/hero-mobile.webp';
      document.head.appendChild(heroImage);
    }
  }, 1000);
}

/**
 * Adaptive loading based on device capabilities
 */
export function getAdaptiveLoadingStrategy(): {
  chunkSize: 'small' | 'medium' | 'large';
  preloadStrategy: PreloadStrategy;
  maxConcurrentLoads: number;
} {
  const connection = (navigator as any).connection;
  const hardwareConcurrency = navigator.hardwareConcurrency || 2;
  const deviceMemory = (navigator as any).deviceMemory || 4;

  // Determine device capability
  const isLowEndDevice = hardwareConcurrency < 4 || deviceMemory < 4;
  const isSlowConnection = connection && (
    connection.effectiveType === '2g' || 
    connection.effectiveType === 'slow-2g' ||
    connection.downlink < 1.5
  );

  if (isLowEndDevice || isSlowConnection) {
    return {
      chunkSize: 'small',
      preloadStrategy: 'never',
      maxConcurrentLoads: 1,
    };
  }

  if (connection && connection.effectiveType === '3g') {
    return {
      chunkSize: 'medium',
      preloadStrategy: 'hover', // Changed from viewport to hover for better performance
      maxConcurrentLoads: 2,
    };
  }

  // Even for fast connections, be more conservative to improve LCP
  return {
    chunkSize: 'medium', // Changed from large to medium
    preloadStrategy: 'hover', // Changed from idle to hover
    maxConcurrentLoads: 3, // Reduced from 4 to 3
  };
}